/**
 * useShellFight - changing the phase, and starting a fight.
 *
 * Two things the bare phase switch does not do:
 *
 * - EVERY CHANGE OF PHASE CLEARS THE SCENE'S PROMISES. Aim, a feint, the
 *   stances and Overwatch were made in the scene that just ended, and the
 *   per-round tick flags belong to its rounds. `direction.setPhase` is
 *   wrapped so the header's plate does this without knowing.
 * - `startFight(plan)` is the fuller door into Combat
 *   (`engine/phase/startFight`): where the sides stand, the order (one
 *   undoable step), who is surprised, then the phase, and a line in the log.
 *   Coming out of Stealth, whoever had not noticed the party goes in
 *   Unaware (`engine/unaware`), and at the ladder's lowest rung the dialog
 *   opens on the opposition surprised (`fightSides.caughtUnaware`).
 *
 * Only whoever directs the table calls either; the rules refuse anyone
 * else's write of the frame.
 *
 * @param {object} s - the shell scope: roster, tabs, updateTab,
 *   updateCombatantState, direction, turnTracker, sheetForCombatant, tokens,
 *   writeToken, terrain, mapSolids, addNarrative, activeTab.
 * @returns {{ direction, startFight, fightSides, mapModeRequest }}
 */
import { actingByFrom } from '../engine/roster/actingBy';
import { unawareIntoFight, sidesOf } from '../engine/phase/sides';
import { toMs } from '../utils/timestamps';
import { useCallback, useMemo, useState } from 'react';
import { dropOnPhaseChange } from '../engine/preparedStates';
import { isInTheFight } from '../engine/roster/inTheFight';
import { fightLine, orderRunning, placeSidesApart, sitsOutUnder } from '../engine/phase/startFight';
import { clearTickFlags } from './turnTrackerTurn';
import { layoutOf } from '../engine/phase/layout';
import { stealthOf } from '../engine/phase/frame';
import { madeUnaware } from '../engine/unaware';

/** Drop every prepared state on the roster; only someone who carries one is written. */
function clearPromises(roster, updateCombatantState) {
  for (const c of roster) {
    if (dropOnPhaseChange(c.state?.conditions ?? []).removed.length === 0) continue;
    updateCombatantState(c.id, (st) => ({ ...st, conditions: dropOnPhaseChange(st.conditions ?? []).conds }));
  }
}

/** The fuller door into Combat: place, open the order, change the phase, say so. */
function useStartFight(s, { fightSides, setPhase, setMapModeRequest }) {
  const { roster, turnTracker, sheetForCombatant, tokens, writeToken, terrain, mapSolids, addNarrative, activeTab } = s;
  const { updateCombatantState } = s;
  return useCallback(
    (plan) => {
      if (plan.placement === 'apart' && terrain?.boundary) {
        const ground = { boundary: terrain.boundary, solids: mapSolids };
        for (const token of placeSidesApart(tokens, fightSides, plan.distance, ground)) {
          writeToken(token, { immediate: true });
        }
      }
      // With no party named there are no sides, but there is still a fight: everyone standing is in it.
      const everyone = roster.filter(isInTheFight);
      const opened = turnTracker.openFight(
        everyone.map((c) => ({ combatantId: c.id, name: c.name, sheet: sheetForCombatant(c.id) })),
        { reroll: plan.rerollInitiative, sitsOut: sitsOutUnder(plan, fightSides), keep: plan.keepOrder },
      );
      // Whoever the scene had not noticing the party goes into the fight Unaware: Easy to hit and
      // unable to react until attacked, or until their own first turn. Read before the phase moves.
      // Never somebody's own character - see `unawareIntoFight`.
      const played = new Set(Object.values(actingByFrom(s.connectedPlayers ?? [], toMs)));
      const first = opened?.participants[opened.activeIndex]?.combatantId ?? null;
      const unaware = unawareIntoFight(s.unawareIds ?? [], fightSides, played, first);
      setPhase('combat');
      for (const id of unaware) {
        updateCombatantState(id, (st) => ({ ...st, conditions: madeUnaware(st.conditions) }));
      }
      if (plan.placement === 'byHand') setMapModeRequest((was) => ({ mode: 'deploy', key: (was?.key ?? 0) + 1 }));
      addNarrative(activeTab, {
        type: 'info',
        lane: 'story',
        text: fightLine(plan, fightSides),
        timestamp: Date.now(),
      });
    },
    [
      terrain,
      mapSolids,
      tokens,
      fightSides,
      writeToken,
      roster,
      turnTracker,
      sheetForCombatant,
      setPhase,
      setMapModeRequest,
      addNarrative,
      activeTab,
      s.unawareIds,
      s.connectedPlayers,
      updateCombatantState,
    ],
  );
}

export default function useShellFight(s) {
  const { roster, tabs, updateTab, updateCombatantState, direction } = s;
  /** Ask the map for a mode: `{ mode, key }`, the key so the same request can be made twice. */
  const [mapModeRequest, setMapModeRequest] = useState(null);
  const partyTeam = direction.frame?.partyTeam ?? null;
  // The sides, and whether the scene has left the opposition unaware of the party (Stealth at Unaware).
  const caughtUnaware =
    !!direction.frame && layoutOf(direction.frame.phase).wearsRung && stealthOf(direction.frame).rung === 'unaware';
  // Whether there is an order to keep: Start the fight then asks Keep or Restart (2026-10-28).
  const tracker = s.turnTracker?.trackerState;
  const running = !!tracker && orderRunning(tracker);
  const fightSides = useMemo(
    () => ({ ...sidesOf(roster, partyTeam), caughtUnaware, orderRunning: running }),
    [roster, partyTeam, caughtUnaware, running],
  );

  const { phase, setPhase: setBarePhase } = direction;
  const setPhase = useCallback(
    (next) => {
      if (next === phase) return true;
      const moved = setBarePhase(next);
      if (!moved) return false;
      clearPromises(roster, updateCombatantState);
      clearTickFlags({ tabs, updateTab });
      return true;
    },
    [phase, setBarePhase, roster, updateCombatantState, tabs, updateTab],
  );

  const startFight = useStartFight(s, { fightSides, setPhase, setMapModeRequest });
  const wrapped = useMemo(() => ({ ...direction, setPhase }), [direction, setPhase]);
  return { direction: wrapped, startFight, fightSides, mapModeRequest };
}
